import { z } from 'zod';

export const MasteryThresholdSchema = z.object({
  // Maximum number of self-corrected mistakes allowed before chapter test fails.
  maxSelfCorrectedMistakes: z.number().int().min(0).max(20).default(1),
  // Maximum number of "uncorrected" mistakes (where learner needed feedback to recover).
  maxUncorrectedMistakes: z.number().int().min(0).max(20).default(0),
  // Required fraction of correct first-attempts in chapter test [0..1].
  minFirstAttemptCorrect: z.number().min(0).max(1).default(1),
  // After this many side-step visits on the same main step, skip stage-1 feedback.
  sideStepDirectFeedbackAfter: z.number().int().min(1).max(10).default(2),
});
export type MasteryThreshold = z.infer<typeof MasteryThresholdSchema>;

// Chapter draft/publish: a draft is an ordinary chapter row pointing back
// at the live chapter it's a draft *of* via draftForChapterId. 'status'
// only matters for a draft row (gates who can read it — see the API's
// chapter routes); a live chapter is always effectively "published" by
// virtue of being the live one.
export const ChapterStatusSchema = z.enum(['draft', 'review', 'published']);
export type ChapterStatus = z.infer<typeof ChapterStatusSchema>;

// Chapter share links: 'private' (default) = not shared. 'public' = anyone
// with the link, no account. 'authenticated' = any valid platform account,
// no enrollment required. Only ever set on a live chapter, never a draft.
export const ChapterShareModeSchema = z.enum(['private', 'public', 'authenticated']);
export type ChapterShareMode = z.infer<typeof ChapterShareModeSchema>;

// Response shape for the owner-only share-management routes — includes the
// raw token (unlike ChapterSchema below, which never carries it).
export const ChapterShareStateSchema = z.object({
  shareMode: ChapterShareModeSchema,
  shareToken: z.string().nullable(),
  shareUrl: z.string().nullable(),
});
export type ChapterShareState = z.infer<typeof ChapterShareStateSchema>;

// Response shape for the public, unauthenticated share-resolve route.
export const PublicShareInfoSchema = z.object({
  chapterId: z.string().uuid(),
  chapterTitle: z.string(),
  projectName: z.string(),
  shareMode: ChapterShareModeSchema,
});
export type PublicShareInfo = z.infer<typeof PublicShareInfoSchema>;

// Audio-freshness indicator for the chapter list — only computed for the
// owner (see GET /versions/:versionId/chapters). `missing` is the reliable
// "needs a regen" signal (TTS audio is content-addressed by text hash, so a
// changed text simply has no matching row yet); the two timestamps are
// supplementary context, not authoritative on their own (steps.updatedAt
// bumps on any field edit, not just narrated text).
export const ChapterTtsStatusSchema = z.object({
  total: z.number().int().min(0),
  missing: z.number().int().min(0),
  lastGeneratedAt: z.string().datetime().nullable(),
  lastTextChangeAt: z.string().datetime().nullable(),
});
export type ChapterTtsStatus = z.infer<typeof ChapterTtsStatusSchema>;

export const ChapterSchema = z.object({
  id: z.string().uuid(),
  projectVersionId: z.string().uuid(),
  ordinal: z.number().int().min(0),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).default(''),
  startStepId: z.string().uuid().nullable(), // entry-point step in the script graph
  masteryThreshold: MasteryThresholdSchema,
  draftForChapterId: z.string().uuid().nullable().default(null),
  status: ChapterStatusSchema.default('published'),
  versionLabel: z.string().min(1).max(40).default('1.0'),
  shareMode: ChapterShareModeSchema.default('private'),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Computed, only present on some responses (never persisted on the row
  // itself): when fetching a *live* chapter as its owner, the id of its
  // active draft (if any); when fetching a *draft*, the live chapter it
  // belongs to, for the Studio banner.
  draftChapterId: z.string().uuid().nullable().optional(),
  liveChapter: z.object({
    id: z.string().uuid(),
    title: z.string(),
    versionLabel: z.string(),
  }).nullable().optional(),
  // Owner-only, only present on the chapter-list response.
  ttsStatus: ChapterTtsStatusSchema.optional(),
});
export type Chapter = z.infer<typeof ChapterSchema>;

export const CreateChapterRequestSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
});
export type CreateChapterRequest = z.infer<typeof CreateChapterRequestSchema>;
